import { HttpClient, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { AuthConfigService } from "../modules/auth/_services/auth-config.service";
import { Response } from "../shared/models/Response";

export interface UsuarioElegibleReemplazante {
   usuarioId: string;
   nombre: string;
   apellidos: string;
   email: string;
   emisorIds: string[];
   activo: boolean;
}

export interface EmpresaCambioUsuarioBase {
   emisorId: string;
   razonSocial: string;
   hayCupo: boolean;
   esIndependiente: boolean;
   usuariosParaDesactivar: UsuarioElegibleReemplazante[];
}

export interface CambioUsuarioBase {
   cambioUsuarioBaseId: string;
   usuarioBaseActualId: string;
   usuarioReemplazanteId?: string;
   emailReemplazante: string;
   reemplazanteNombre?: string;
   reemplazanteApellidos?: string;
   esUsuarioNuevo: boolean;
   estadoCodigo: string;
   emailVerificadoEn?: string;
   aceptoTerminos: boolean;
   fechaSolicitud: string;
   fechaExpiracion?: string;
   fechaCompletada?: string;
   empresas: EmpresaCambioUsuarioBase[];
   todasConCupo: boolean;
   requiereDesactivarUsuarioBaseActual: boolean;
}

export interface ContextoPlanCambioUsuarioBase {
   esUsuarioBaseVigente: boolean;
   empresas: EmpresaCambioUsuarioBase[];
   usuariosElegibles: UsuarioElegibleReemplazante[];
   cambioPendiente?: CambioUsuarioBase;
   requiereDesactivarUsuarioBaseActual: boolean;
}

export interface NuevoReemplazanteRequest {
   rut: number;
   nombre: string;
   apellidos: string;
   email: string;
}

export interface TerminoCondicionesCambioUsuario {
   versionTerminos: string;
   nombreArchivo: string;
   fechaCreacion: string;
}

@Injectable({
   providedIn: "root",
})
export class CambioUsuarioBaseService {
   private readonly endpoint: string;
   private readonly terminosEndpoint: string;

   constructor(private http: HttpClient, private config: AuthConfigService) {
      this.endpoint = `${this.config.wepApiRoute}/CambioUsuarioBase`;
      this.terminosEndpoint = `${this.config.wepApiRoute}/TerminosCondiciones`;
   }

   obtenerContexto(): Observable<Response<ContextoPlanCambioUsuarioBase>> {
      return this.http.get<Response<ContextoPlanCambioUsuarioBase>>(`${this.endpoint}/contexto`);
   }

   obtenerPendiente(): Observable<Response<CambioUsuarioBase | null>> {
      return this.http.get<Response<CambioUsuarioBase | null>>(`${this.endpoint}/pendiente`);
   }

   iniciar(): Observable<Response<CambioUsuarioBase>> {
      return this.http.post<Response<CambioUsuarioBase>>(`${this.endpoint}/iniciar`, {});
   }

   confirmarVerificacionEmail(cambioId: string, codigo: string): Observable<Response<boolean>> {
      return this.http.post<Response<boolean>>(`${this.endpoint}/${cambioId}/verificar-email/confirmar`, { codigo });
   }

   seleccionarReemplazanteExistente(cambioId: string, usuarioReemplazanteId: string): Observable<Response<CambioUsuarioBase>> {
      return this.http.post<Response<CambioUsuarioBase>>(`${this.endpoint}/${cambioId}/reemplazante/existente`, {
         usuarioReemplazanteId,
      });
   }

   seleccionarReemplazanteNuevo(cambioId: string, datos: NuevoReemplazanteRequest): Observable<Response<CambioUsuarioBase>> {
      return this.http.post<Response<CambioUsuarioBase>>(`${this.endpoint}/${cambioId}/reemplazante/nuevo`, datos);
   }

   liberarCupo(cambioId: string, emisorId: string, usuarioADesactivarId: string): Observable<Response<CambioUsuarioBase>> {
      return this.http.post<Response<CambioUsuarioBase>>(`${this.endpoint}/${cambioId}/liberar-cupo`, {
         emisorId,
         usuarioADesactivarId,
      });
   }

   liberarCupoBase(cambioId: string): Observable<Response<CambioUsuarioBase>> {
      return this.http.post<Response<CambioUsuarioBase>>(`${this.endpoint}/${cambioId}/liberar-cupo-base`, {});
   }

   obtenerTerminos(): Observable<Response<TerminoCondicionesCambioUsuario>> {
      return this.http.get<Response<TerminoCondicionesCambioUsuario>>(`${this.terminosEndpoint}`);
   }

   descargarTerminosPdf(): Observable<HttpResponse<Blob>> {
      return this.http.get(`${this.terminosEndpoint}/pdf`, { observe: "response", responseType: "blob" });
   }

   confirmar(cambioId: string, versionTerminos: string, aceptaTerminos: boolean): Observable<Response<boolean>> {
      return this.http.post<Response<boolean>>(`${this.endpoint}/${cambioId}/confirmar`, {
         versionTerminos,
         aceptaTerminos,
      });
   }

   cancelar(cambioId: string, motivo: string): Observable<Response<boolean>> {
      return this.http.post<Response<boolean>>(`${this.endpoint}/${cambioId}/cancelar`, { motivo });
   }
}
